<div class="p-4">
  <mat-dialog-content>
    @if (!loading()) {
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-2">
          @switch (type()) {
            @case (modalType.ManualCreation) {
              <h3>{{ "laboratoryOrder.label.create" | translate }}</h3>
            }
            @case (modalType.BindToExisting) {
              <h3>{{ "laboratoryOrder.label.bind" | translate }}</h3>
            }
          }
          <button mat-dialog-close>
            <mat-icon
              aria-hidden="true"
              aria-label="Close icon"
              fontIcon="close"
            ></mat-icon>
          </button>
        </div>

        <form [formGroup]="createLaboratoryOrderForm">
          @if (data.prescriptions.length === 1) {
            <mat-form-field class="w-full">
              <mat-label>{{ "laboratoryOrder.label.type" | translate }}</mat-label>
              <input
                matInput
                readonly
                [type]="'text'"
                [value]="'medicalServices.prescriptionTypes.' + prescriptionTypes.LaboratoryService | translate"
              />
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>{{ "laboratoryOrder.label.name" | translate }}</mat-label>
              <input
                matInput
                readonly
                [type]="'text'"
                [value]="fc.orderName.value || ('laboratoryOrder.message.serviceNotSpecified' | translate)"
              />
            </mat-form-field>
          } @else {
            <div class="mb-4 w-full">
              <mat-label class="text-gray-600 mb-2 text-sm font-medium">{{ "laboratoryOrder.label.selectedPrescriptions" | translate }}</mat-label>
              <div class="flex max-h-80 flex-col gap-1 overflow-y-auto">
                @for (prescription of data.prescriptions; track prescription.id) {
                  <prescriptions-modal-item
                    [prescription]="{
                      id: prescription.id,
                      name: prescription.name,
                      franchise: prescription.franchise,
                      type: prescriptionTypes.LaboratoryService,
                    }"
                  />
                }
              </div>
            </div>
          }

          @if (type() !== modalType.Cancel) {
            <mat-form-field class="relative w-full">
              <mat-label>{{ "laboratoryOrder.label.partnerName" | translate }}</mat-label>
              <input
                matInput
                type="text"
                [formControl]="fc.partnerName"
                [matAutocomplete]="partnersAutocomplete"
                [placeholder]="'laboratoryOrder.label.partnerSearchPlaceholder' | translate"
              />
              @if (partnersLoading()) {
                <div class="pointer-events-none absolute inset-y-0 left-1/2 z-10 flex -translate-x-1/2 transform items-center">
                  <shared-spin-loading [size]="16"></shared-spin-loading>
                </div>
              } @else if (fc.partnerName.value) {
                <button
                  matSuffix
                  mat-icon-button
                  type="button"
                  aria-label="Clear"
                  (click)="clearPartnersSearch()"
                >
                  <mat-icon matIconSuffix>close</mat-icon>
                </button>
              }
              <mat-autocomplete
                #partnersAutocomplete
                (optionSelected)="selectPartnerByName($event.option.value)"
              >
                <div
                  sharedInfiniteScroll
                  class="max-h-60 overflow-auto"
                  [scrollLoading]="partnersLoading()"
                  [hasNextPage]="!!partnersContinuationToken()"
                  (fetchNextPage)="fetchPartnersNextPage()"
                >
                  @for (partner of partnerOptions(); track partner.id) {
                    <mat-option [value]="partner.name">
                      {{ partner.name }}
                    </mat-option>
                  }
                  @if (partnersLoading()) {
                    <div class="flex w-full items-center justify-center">
                      <shared-loader></shared-loader>
                    </div>
                  }
                </div>
              </mat-autocomplete>
              @if (fc.partnerId.invalid && fc.partnerId.touched) {
                <mat-error>{{ "validation.error.required" | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field class="relative w-full">
              <mat-label>{{ "laboratoryOrder.label.departmentName" | translate }}</mat-label>
              <input
                matInput
                type="text"
                [formControl]="fc.locationName"
                [matAutocomplete]="locationsAutocomplete"
                [placeholder]="'laboratoryOrder.label.locationSearchPlaceholder' | translate"
              />
              @if (laboratoriesLoading()) {
                <div class="pointer-events-none absolute inset-y-0 left-1/2 z-10 flex -translate-x-1/2 transform items-center">
                  <shared-spin-loading [size]="16"></shared-spin-loading>
                </div>
              } @else if (fc.locationName.value) {
                <button
                  matSuffix
                  mat-icon-button
                  type="button"
                  aria-label="Clear"
                  (click)="clearLaboratoriesSearch()"
                >
                  <mat-icon matIconSuffix>close</mat-icon>
                </button>
              }
              <mat-autocomplete
                #locationsAutocomplete
                (optionSelected)="selectLocationById($event.option.value)"
              >
                <div class="max-h-60 overflow-auto">
                  @for (laboratory of laboratoryOptions(); track laboratory.locationId) {
                    <mat-option [value]="laboratory.locationId">
                      <div class="flex flex-col items-start gap-0.5 py-1">
                        <span>{{ laboratory.locationName }}</span>
                        <span class="text-sm text-gray-2">{{ laboratory.locationAddress }}</span>
                      </div>
                    </mat-option>
                  }
                  @if (laboratoriesLoading()) {
                    <div class="flex w-full items-center justify-center">
                      <shared-loader></shared-loader>
                    </div>
                  }
                </div>
              </mat-autocomplete>
              @if (fc.locationId.invalid && fc.locationId.touched) {
                <mat-error>{{ "validation.error.required" | translate }}</mat-error>
              }
            </mat-form-field>

            @if (hasIntegration() && fc.locationId.value) {
              <!-- Slots Selection for Integrated Partners -->
              <div class="mb-4 w-full">
                <label class="text-gray-700 mb-2 block text-sm font-medium">
                  {{ "laboratoryOrder.label.availableSlots" | translate }}
                </label>
                <shared-time-slots
                  [slots]="availableSlots()"
                  [loading]="slotsLoading()"
                  [selectedSlot]="selectedSlot()"
                  (slotSelected)="selectSlot($event)"
                />
                @if (selectedSlot()) {
                  <span class="text-gray-700 text-sm">
                    <span class="font-semibold">{{ "laboratoryOrder.label.workingHours" | translate }}</span>
                    {{ getSelectedSlotWorkingHours() }}
                  </span>
                }
              </div>
            } @else {
              <!-- Manual Date/Time Selection for Non-Integrated Partners -->
              <div class="flex w-full gap-4">
                <mat-form-field>
                  <mat-label>{{ "laboratoryOrder.label.date" | translate }}</mat-label>
                  <input
                    matInput
                    [matDatepicker]="datepicker"
                    [formControl]="fc.date"
                  />
                  <mat-datepicker #datepicker />
                  <mat-datepicker-toggle
                    [for]="datepicker"
                    matSuffix
                  />
                  @if (fc.date.invalid && fc.date.touched) {
                    <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ "location.label.from" | translate }}</mat-label>
                  <input
                    matInput
                    [matTimepicker]="fromTimepicker"
                    [formControl]="fc.startTime"
                  />
                  <mat-timepicker #fromTimepicker />
                  <mat-timepicker-toggle
                    matSuffix
                    [for]="fromTimepicker"
                  />

                  @if (fc.startTime.invalid && fc.startTime.touched) {
                    <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field>
                  <mat-label>{{ "location.label.to" | translate }}</mat-label>
                  <input
                    matInput
                    [matTimepicker]="toTimepicker"
                    [formControl]="fc.endTime"
                  />
                  <mat-timepicker #toTimepicker />
                  <mat-timepicker-toggle
                    matSuffix
                    [for]="toTimepicker"
                  />
                  @if (fc.endTime.invalid && fc.endTime.touched) {
                    <mat-error>{{ "validation.error.required" | translate }}</mat-error>
                  }
                </mat-form-field>
              </div>
            }

            <mat-form-field class="w-full">
              <mat-label>{{ "laboratoryOrder.label.price" | translate }}</mat-label>
              <input
                matInput
                type="number"
                min="0"
                step="0.01"
                [formControl]="fc.price"
              />
              @if (fc.price.invalid && fc.price.touched) {
                <mat-error>{{ "validation.error.required" | translate }}</mat-error>
              }
            </mat-form-field>

            @if (showOrderNumber()) {
              <mat-form-field class="w-full">
                <mat-label>{{ "laboratoryOrder.label.orderNumber" | translate }}</mat-label>
                <input
                  matInput
                  type="text"
                  [formControl]="fc.orderNumber"
                />
              </mat-form-field>
            }
          } @else {
            <shared-warning-message
              [text]="'laboratoryOrder.message.cancellationWarning' | translate: { partner: partnerName(), location: address() }"
              [subText]="'laboratoryOrder.message.cancellationDetails' | translate: { dateTime: startDate() }"
            ></shared-warning-message>
          }
        </form>
      </div>
    } @else {
      <div class="flex items-center justify-center">
        <shared-loader></shared-loader>
      </div>
    }
  </mat-dialog-content>
  <mat-dialog-actions class="gap-4">
    @switch (type()) {
      @case (modalType.Cancel) {
        <button
          class="btn btn-primary"
          [type]="'button'"
          (click)="submit()"
        >
          {{ "prescription.action.cancelLaboratoryOrder" | translate }}
        </button>
      }
      @default {
        <button
          class="btn btn-secondary"
          mat-dialog-close
          [type]="'button'"
        >
          {{ "buttons.cancel" | translate }}
        </button>
        <button
          class="btn btn-primary"
          [type]="'button'"
          [disabled]="isSubmitDisabled()"
          (click)="submit()"
        >
          {{ "buttons.confirm" | translate }}
        </button>
      }
    }
  </mat-dialog-actions>
</div>
